/**
 * RADIUS "secret follows the host" rule -- the client mirror of the backend
 * services/radius_destination (D-18 follow-up, owner decision 2026-10-09, OP #1711).
 *
 * A stored RADIUS shared secret may only keep going to the host it was entered for. Moving it
 * to a different host needs a customer admin (org_admin) or above AND a re-entered secret;
 * a port-only edit on the same host is not a move. The SSID and Guest Portal dialogs use
 * this to (a) lock the host field for a site manager when a secret is stored and (b) make
 * the secret field required, with the reason, when an admin changes the host.
 *
 * UX only: the server enforces the same rule (403 / 400) whatever the dialog does.
 * Pure functions, no React -- unit-tested by radiusDestination.test.ts (`npm run test:unit`).
 */

/** Host as compared by the backend: trimmed, lower-cased; blank -> "" (no destination). */
export function normHost(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/**
 * Would this edit send a STORED secret to a different host?
 *
 * Only a leg that has a stored secret can be repointed, and removing a leg (blank new host)
 * is not a move -- same as the backend's per-leg check.
 */
export function hostMoved(
  wasHost: string | null | undefined,
  nowHost: string | null | undefined,
  hasStoredSecret: boolean | undefined,
): boolean {
  if (!hasStoredSecret) return false;
  const now = normHost(nowHost);
  return now !== "" && now !== normHost(wasHost);
}

/** The backend's 400 wording, so the dialog says exactly what the server would. */
export const REENTER_HINT = "re-enter it: the host changed";

/** Shown on a locked host field (site manager, stored secret). */
export const HOST_LOCKED_HINT =
  "Only a customer admin can move this RADIUS server to a new host, because the stored shared " +
  "secret would be sent there. Pick a registered server, or ask a customer admin.";
